import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

import Logo from "../../utils/assets/img/Logo-rosa.png";

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontSize: 10,
    color: "#111827",
    fontFamily: "Helvetica",
  },

  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingBottom: 12,
    marginBottom: 16,
  },

  headerLeft: {
    flex: 1,
    paddingRight: 16,
    justifyContent: "center",
  },

  logo: {
    width: 120,
    height: 80,
    objectFit: "contain",
  },

  companyName: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 4,
    color: "#902348",
  },

  companyText: {
    fontSize: 9,
    lineHeight: 1.4,
    color: "#4B5563",
  },

  titleRow: {
    marginBottom: 14,
  },

  title: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 4,
  },

  subtitle: {
    fontSize: 10,
    color: "#6B7280",
  },

  sectionGrid: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 14,
  },

  box: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 6,
    padding: 10,
  },

  boxTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 6,
    color: "#902348",
  },

  boxText: {
    fontSize: 9,
    lineHeight: 1.5,
  },

  table: {
    marginTop: 4,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 6,
    overflow: "hidden",
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 8,
    paddingHorizontal: 8,
  },

  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
    paddingVertical: 8,
    paddingHorizontal: 8,
  },

  colProduct: {
    flex: 2.2,
    paddingRight: 6,
  },

  colCode: {
    flex: 1,
    paddingRight: 6,
  },

  colQty: {
    flex: 0.7,
    textAlign: "right",
    paddingRight: 6,
  },

  colPrice: {
    flex: 1,
    textAlign: "right",
    paddingRight: 6,
  },

  colTotal: {
    flex: 1,
    textAlign: "right",
  },

  th: {
    fontSize: 9,
    fontWeight: "bold",
  },

  td: {
    fontSize: 8.5,
    lineHeight: 1.4,
  },

  totalWrap: {
    marginTop: 14,
    alignItems: "flex-end",
  },

  totalBox: {
    width: 190,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 6,
    padding: 10,
  },

  totalLabel: {
    fontSize: 9,
    color: "#6B7280",
    marginBottom: 4,
  },

  totalValue: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#111827",
  },

  footer: {
    marginTop: 18,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    paddingTop: 10,
  },

  footerText: {
    fontSize: 8.5,
    color: "#6B7280",
    lineHeight: 1.4,
  },

  boxTextId: {
    fontSize: 7,
    color: "#9CA3AF",
    marginTop: 6,
    letterSpacing: 0.5,
  },
  customBlock: {
    marginTop: 6,
    marginBottom: 6,
  },

  customTitle: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#6B7280",
    marginBottom: 2,
  },

  customItem: {
    fontSize: 8.5,
    color: "#902348",
    fontWeight: "bold",
    lineHeight: 1.4,
  },

});

function moneyBRL(v) {
  return Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatDateTime(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}


function getItemProductType(item) {
  return item?.productType || item?.chainType || "-";
}

function normalizePhoneDigits(rawValue) {
  let digits = String(rawValue || "").replace(/\D/g, "");

  if (digits.startsWith("55")) {
    digits = digits.slice(2);
  }

  if (digits.length > 11) {
    digits = digits.slice(-11);
  }

  return digits;
}

function formatPhoneBR(value) {
  const digits = normalizePhoneDigits(value);

  if (!digits) return "-";

  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d{1,4})$/, "$1-$2");
  }

  return digits
    .replace(/^(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d{1,4})$/, "$1-$2");
}

function normalizeCustomTexts(customTexts) {
  if (!Array.isArray(customTexts)) return [];

  return customTexts
    .map((entry) => {
      if (!entry) return null;

      if (typeof entry === "string") {
        return {
          label: "Campo",
          value: entry,
        };
      }

      if (typeof entry === "object") {
        return {
          label:
            entry.label ||
            entry.name ||
            entry.field ||
            entry.key ||
            entry.placeholder ||
            "Campo",
          value:
            entry.value ||
            entry.text ||
            entry.content ||
            "",
        };
      }

      return null;
    })
    .filter(Boolean)
    .filter((entry) => String(entry.value || "").trim());
}

export default function OrderPdfDocument({
  company = {},
  seller = {},
  customer = {},
  items = [],
  total = 0,
  orderCode = "",
  createdAt = new Date(),
  logoSrc = "",
}) {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.companyName}>
              {company.name || "Pitaya"}
            </Text>
            <Text style={styles.companyText}>
              {company.subtitle || "Pedido de joias personalizadas"}
            </Text>
            {!!company.phone && (
              <Text style={styles.companyText}>WhatsApp: {formatPhoneBR(company.phone)}</Text>
            )}
            {!!company.email && (
              <Text style={styles.companyText}>E-mail: {company.email}</Text>
            )}
            {!!company.website && (
              <Text style={styles.companyText}>Site: {company.website}</Text>
            )}
          </View>

          <Image src={logoSrc || Logo} style={styles.logo} />
        </View>

        <View style={styles.titleRow}>
          <Text style={styles.title}>Pedido</Text>
          <Text style={styles.subtitle}>
            Código: {orderCode || "-"} | Gerado em: {formatDateTime(createdAt)}
          </Text>
        </View>

        <View style={styles.sectionGrid}>
          <View style={styles.box}>
            <Text style={styles.boxTitle}>Cliente</Text>
            <Text style={styles.boxText}>
              Nome: {customer?.name || "-"}
            </Text>
            <Text style={styles.boxText}>
              WhatsApp: {formatPhoneBR(customer?.phone)}
            </Text>
          </View>

          <View style={styles.box}>
            <Text style={styles.boxTitle}>Vendedora</Text>
            <Text style={styles.boxText}>
              Nome: {seller?.display_name || seller?.name || "-"}
            </Text>
            <Text style={styles.boxText}>
              WhatsApp: {formatPhoneBR(seller?.whatsapp)}
            </Text>
            {!!seller?.seller_id && (
              <Text style={styles.boxTextId}>
                {seller.seller_id}
              </Text>
            )}
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.th, styles.colProduct]}>Produto</Text>
            <Text style={[styles.th, styles.colCode]}>Código</Text>
            <Text style={[styles.th, styles.colQty]}>Qtd</Text>
            <Text style={[styles.th, styles.colPrice]}>Unit.</Text>
            <Text style={[styles.th, styles.colTotal]}>Total</Text>
          </View>

          {items.map((item, index) => (
            <View key={`${item.optionId || item.code}-${index}`} style={styles.tableRow}>
              <View style={styles.colProduct}>
                <Text style={styles.td}>{item.productName || "-"}</Text>
                {(() => {
                  const customFields = normalizeCustomTexts(item?.customTexts);

                  if (customFields.length) {
                    return (
                      <View style={styles.customBlock}>
                        <Text style={styles.customTitle}>Personalização</Text>

                        {customFields.map((entry, idx) => (
                          <Text key={`${entry.label}-${idx}`} style={styles.customItem}>
                            {entry.label}: {entry.value}
                          </Text>
                        ))}
                      </View>
                    );
                  }

                  if (item?.customText) {
                    return (
                      <Text style={styles.customItem}>
                        Personalização: {item.customText}
                      </Text>
                    );
                  }

                  return null;
                })()}
                <Text style={styles.td}>
                  {getItemProductType(item)} • {item.color || "-"}
                </Text>
              </View>

              <Text style={[styles.td, styles.colCode]}>
                {item.code || "-"}
              </Text>

              <Text style={[styles.td, styles.colQty]}>
                {item.quantity || 1}
              </Text>

              <Text style={[styles.td, styles.colPrice]}>
                {moneyBRL(item.price || 0)}
              </Text>

              <Text style={[styles.td, styles.colTotal]}>
                {moneyBRL((item.price || 0) * (item.quantity || 1))}
              </Text>
            </View>
          ))}
        </View>

        <View style={styles.totalWrap}>
          <View style={styles.totalBox}>
            <Text style={styles.totalLabel}>Total Pedido</Text>
            <Text style={styles.totalValue}>{moneyBRL(total)}</Text>
          </View>
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>
            Este documento representa uma solicitação de pedido enviada pelo catálogo digital.
          </Text>
          <Text style={styles.footerText}>
            <b style={{ color: 'black' }}>Prazo:</b> Produto estará pronto em até 35 dias corridos da confirmação do seu pedido.
          </Text>
          <Text style={styles.footerText}>
            Código para consulta: {orderCode || "-"}
          </Text>
        </View>
      </Page>
    </Document>
  );
}